/* ==========================================================================
   AB motion layer — behaviour
   No dependencies. Everything is IntersectionObserver driven so nothing
   animates or downloads while off screen.

   Responsibilities:
     1. Reveal elements as they enter the viewport.
     2. Pause the infinite orbit animation while it is off screen.
     3. Load and play background video only when it is on screen. Small
        screens get the lighter mobile encode when the section supplies one,
        and the poster image only when it does not. Save-data, 2g, and
        reduced-motion always get the poster and never fetch video.

   The page must be fully usable with this file absent or failed.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.documentElement;

  var prefersReduced = window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var hasIO = 'IntersectionObserver' in window;

  /* Only opt into the hidden-then-revealed pattern when we can actually
     reveal things. Otherwise content would be stuck invisible. */
  if (hasIO && !prefersReduced) {
    root.classList.add('ab-motion');
  }

  /* --------------------------------------------------------------------
     Should this visitor get video at all?
     Mobile is the case the client cares about most, so it defaults to the
     poster image and never pays for the download.
     -------------------------------------------------------------------- */
  function isSmall() {
    return window.innerWidth < 750;
  }

  function videoAllowed() {
    if (prefersReduced) return false;

    var c = navigator.connection;
    if (c) {
      if (c.saveData) return false;
      if (typeof c.effectiveType === 'string' && /(^|-)2g$/.test(c.effectiveType)) return false;
    }
    return true;
  }

  /* --------------------------------------------------------------------
     1. Reveals
     -------------------------------------------------------------------- */
  function initReveals() {
    var nodes = document.querySelectorAll('[data-ab-reveal]');
    if (!nodes.length) return;

    if (!hasIO || prefersReduced) {
      for (var i = 0; i < nodes.length; i++) nodes[i].classList.add('is-revealed');
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        el.classList.add('is-revealed');
        io.unobserve(el);
        /* Release the compositor layer once the transition has finished. */
        el.addEventListener('transitionend', function onEnd() {
          el.classList.add('is-settled');
          el.removeEventListener('transitionend', onEnd);
        });
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.01 });

    for (var j = 0; j < nodes.length; j++) io.observe(nodes[j]);

    /* Safety net. The reveal pattern hides content first and shows it when the
       observer fires. Anything that is on screen but still hidden a few seconds
       in is a bug, and the failure mode is blank content in front of the
       client. Sweep once and force those visible. Off-screen elements are left
       alone so the scroll animation still happens where it is seen. */
    function sweep() {
      var stuck = document.querySelectorAll('[data-ab-reveal]:not(.is-revealed)');
      for (var k = 0; k < stuck.length; k++) {
        var r = stuck[k].getBoundingClientRect();
        if (r.bottom > 0 && r.top < (window.innerHeight || 0)) {
          stuck[k].classList.add('is-revealed');
          io.unobserve(stuck[k]);
        }
      }
      return stuck.length;
    }
    window.setTimeout(sweep, 2500);

    /* Second net, independent of the observer: on every scroll or resize (throttled to one
       frame) reveal anything sitting inside the viewport that has not been revealed. Cheap
       (a handful of rect reads) and it stops listening once nothing is left. Exists because
       iOS Safari left a tall section unrevealed while the same page passed in every emulator. */
    var pending = false;
    function onScroll() {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(function () {
        pending = false;
        if (sweep() === 0) {
          window.removeEventListener('scroll', onScroll);
          window.removeEventListener('resize', onScroll);
        }
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
  }

  /* --------------------------------------------------------------------
     2. Orbit — pause while off screen
     -------------------------------------------------------------------- */
  function initOrbits() {
    var orbits = document.querySelectorAll('.ab-orbit-text');
    if (!orbits.length || !hasIO) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        entry.target.classList.toggle('is-paused', !entry.isIntersecting);
      });
    }, { threshold: 0 });

    for (var i = 0; i < orbits.length; i++) io.observe(orbits[i]);
  }

  /* --------------------------------------------------------------------
     3. Background video
     Sources live in data attributes so nothing is fetched until we decide
     this visitor should have it.
     -------------------------------------------------------------------- */
  /* Returns the WebM/MP4 pair this viewport should use, or null when this
     viewport has no usable source and must stay on the poster. */
  function pickSources(video) {
    var webm, mp4;
    if (isSmall()) {
      webm = video.getAttribute('data-src-webm-mobile');
      mp4 = video.getAttribute('data-src-mp4-mobile');
      /* No mobile encode supplied means the desktop file is too heavy to
         push at a phone: poster only, deliberately. */
      if (!webm && !mp4) return null;
    } else {
      webm = video.getAttribute('data-src-webm');
      mp4 = video.getAttribute('data-src-mp4');
      if (!webm && !mp4) return null;
    }
    /* Source order matters: browsers take the first type they can play.
       On the desktop encodes WebM is far smaller, so it leads. On the
       portrait mobile encodes the H.264 file is the smaller of the two, so
       MP4 leads there. */
    return { webm: webm, mp4: mp4, mp4First: isSmall() };
  }

  function buildSources(video, picked) {
    var webm = picked.webm;
    var mp4 = picked.mp4;

    function add(src, type) {
      if (!src) return;
      var s = document.createElement('source');
      s.type = type;
      s.src = src;
      video.appendChild(s);
    }

    if (picked.mp4First) {
      /* af64: phones get ONE source. PSI mobile emulation fetched both encodes (~400KB per clip)
         when both <source> children were present; pick by canPlayType and attach only that one. */
      var v = document.createElement('video');
      var okMp4 = mp4 && v.canPlayType && v.canPlayType('video/mp4; codecs="avc1.42E01E"') !== '';
      if (okMp4) { add(mp4, 'video/mp4'); return; }
      if (webm) { add(webm, 'video/webm'); return; }
      add(mp4, 'video/mp4');
      return;
    }
    add(webm, 'video/webm');
    add(mp4, 'video/mp4');
  }

  function initBackgroundVideo() {
    var videos = document.querySelectorAll('.ab-bgv__video');
    if (!videos.length) return;

    if (!videoAllowed() || !hasIO) {
      /* Poster only. Remove the element so it can never fetch anything. */
      for (var i = 0; i < videos.length; i++) {
        if (videos[i].parentNode) videos[i].parentNode.removeChild(videos[i]);
      }
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var video = entry.target;

        if (entry.isIntersecting) {
          if (!video.dataset.abLoaded) {
            var picked = pickSources(video);
            if (!picked) {
              io.unobserve(video);
              if (video.parentNode) video.parentNode.removeChild(video);
              return;
            }
            video.dataset.abLoaded = '1';
            /* iOS refuses inline autoplay unless muted is true on the element
               itself, not only as an attribute, once sources are swapped in. */
            video.muted = true;
            video.setAttribute('muted', '');
            video.playsInline = true;
            buildSources(video, picked);
            video.load();
            video.addEventListener('playing', function () {
              video.classList.add('is-playing');
            }, { once: true });
          }
          var p = video.play();
          /* Autoplay can still be refused; the poster simply stays. */
          if (p && typeof p.catch === 'function') {
            /* A scroll-scrubbed video (ab-home.js) pauses on 'playing', which rejects this
               promise with AbortError; that is not a refused autoplay, keep it visible. */
            p.catch(function () { if (!video.dataset.abScrub) video.classList.remove('is-playing'); });
          }
        } else if (video.dataset.abLoaded) {
          video.pause();
          /* af72: on phones a paused clip still holds its decoder + frame buffers (iOS Safari kills the page at ~1GB, and
             the Framer's Matrix already does). Tear the sources down once the clip is well off-screen; it re-attaches from
             the HTTP cache when it comes back. The scroll-scrubbed hero (ab-home.js owns its currentTime) is left alone. */
          if (isSmall() && !video.dataset.abScrub) {
            var srcs = video.querySelectorAll('source');
            for (var k = 0; k < srcs.length; k++) video.removeChild(srcs[k]);
            video.removeAttribute('src');
            video.load();
            video.classList.remove('is-playing');
            delete video.dataset.abLoaded;
          }
        }
      });
    }, { rootMargin: isSmall() ? '75% 0px' : '150% 0px', threshold: 0.01 }); /* af30: fetch + start one and a half viewports early so a section never enters black (Framer's clips are already playing when they scroll in). af64: phones use 75% and start observing after window load so the clips never compete with the LCP chain on a throttled line. */

    function observeAll() { for (var j = 0; j < videos.length; j++) io.observe(videos[j]); }
    if (isSmall() && document.readyState !== 'complete') {
      window.addEventListener('load', function () { window.setTimeout(observeAll, 300); }, { once: true });
    } else {
      observeAll();
    }
  }

  function init() {
    initReveals();
    initOrbits();
    initBackgroundVideo();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
